import { useFormContext } from 'react-hook-form';
import { WizardSummary } from '@/components/wizards';
import {
  BUSINESS_TYPE_OPTIONS,
  PRODUCT_VARIANT_OPTIONS,
  VEHICLE_CATEGORY_OPTIONS,
  type MotorQuoteFormData,
} from '../../schema';

interface ReviewStepProps {
  proposerName: string;
  proposerPhone: string;
  proposerEmail?: string;
}

export function ReviewStep({ proposerName, proposerPhone, proposerEmail }: ReviewStepProps) {
  const { watch } = useFormContext<MotorQuoteFormData>();
  const data = watch();

  // The Customer step (item 4b) is the source of truth once a customer has
  // been selected/created — proposerName/Phone/Email props are only a
  // fallback for the brief window before that step has run.
  const displayName = data.customer_name || proposerName;
  const displayPhone = data.customer_phone || proposerPhone;
  const displayEmail = data.customer_email || proposerEmail;

  const vehicleCategoryLabel =
    VEHICLE_CATEGORY_OPTIONS.find((o) => o.value === data.vehicle_category)?.label || '-';
  const businessTypeLabel =
    BUSINESS_TYPE_OPTIONS.find((o) => o.value === data.business_type)?.label || '-';
  const productVariantLabel =
    PRODUCT_VARIANT_OPTIONS.find((o) => o.value === data.product_variant)?.label || '-';

  const sections = [
    {
      title: 'Proposer',
      items: [
        { label: 'Name', value: displayName || '-' },
        { label: 'Phone', value: displayPhone || '-' },
        { label: 'Email', value: displayEmail || '-' },
        // B-02/C1: shown because it is SENT -- it is what the insurer
        // zone-rates the motor premium on, so a wrong one is a wrong number
        // rather than an error, and the broker's last chance to catch it is
        // here.
        { label: 'PIN Code', value: data.customer_pincode || '-' },
      ],
    },
    {
      title: 'Vehicle & Product',
      items: [
        { label: 'Vehicle Type', value: vehicleCategoryLabel },
        { label: 'Business Type', value: businessTypeLabel },
        { label: 'Product Variant', value: productVariantLabel },
        { label: 'Make', value: data.make_name || '-' },
        { label: 'Model', value: data.model_name || '-' },
        { label: 'Variant', value: data.variant_name || '-' },
        { label: 'Fuel Type', value: data.fuel_type || '-' },
      ],
    },
    {
      title: 'Registration',
      items: [
        {
          label: 'Registration Number',
          value:
            data.business_type === 'new' && data.registration_pending
              ? 'Registration Pending'
              : data.registration_number || '-',
        },
        { label: 'Registration Date', value: data.registration_date || '-' },
        { label: 'Manufacturing Year', value: data.manufacturing_year || '-' },
        { label: 'RTO', value: data.rto_label || data.rto_code || '-' },
      ],
    },
  ];

  // Prior Policy only exists for Renewal/Rollover — the PriorPolicyStep
  // never renders (and never collects these) for New business.
  if (data.business_type !== 'new') {
    sections.push({
      title: 'Prior Policy',
      items: [
        { label: 'Previous Policy Number', value: data.previous_policy_number || '-' },
        { label: 'Previous Insurer', value: data.previous_insurer_name || '-' },
        { label: 'Previous Policy Expiry', value: data.previous_policy_expiry || '-' },
        { label: 'Previous NCB', value: data.previous_ncb ? `${data.previous_ncb}%` : '-' },
        { label: 'Claim in Previous Year', value: data.has_claim_in_previous === 'yes' ? 'Yes' : 'No' },
      ],
    });
  }

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Review & Submit</h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-6">
        Confirm the details below before requesting quotes.
      </p>
      <WizardSummary sections={sections} />
    </div>
  );
}
